import React, { useState, useEffect } from 'react';
import { Calendar, Users, CircleDollarSign, DollarSign, TrendingUp, Clock, Ticket, RefreshCw, ChevronDown, ChevronUp, Target, BarChart3, GripVertical, ExternalLink , Shield } from 'lucide-react';
import { Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ComposedChart } from 'recharts';
import MetaCampaigns from './MetaCampaigns';
import { formatDate, formatDaysUntil, calculateDaysUntil, calculateMetrics, formatCurrency, formatCurrencyWithDecimals, formatCurrencyCompact, formatNumber, formatTime, detectTicketStructure, venueTodayStr } from '../utils/helpers';
import { categoryLabel as cl, categoryColor as cc, entryRowOrder, getTicketModel } from '../utils/ticketModel';
import { fetchWidgetDaily, fetchWidgetFunnel, sumWidgetSeries } from '../utils/widgetStats';
import useDynamicRowSpan from '../hooks/useDynamicRowSpan';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const EventCard = ({
  event,
  isLoading,
  loadError,
  isExpanded,
  showAllInPrices,
  revenueDisplayType,
  hasRecentUpdate,
  ticketChange,
  categoryChange = {},
  sectionExpanded,
  expandedAdSets,
  matchedCampaigns,
  widgetStats,
  darkMode,
  cacheTime,
  wsConnected,
  isSubscribed,
  isDragging,
  isPastEvent,
  onToggleExpansion,
  onRefresh,
  onRetry,
  onTogglePriceDisplay,
  onToggleRevenueDisplay,
  onToggleSection,
  onToggleAdSetExpansion,
  occupancyAvailable = false,
  onToggleFlip
}) => {
  // Memoized per event object — every WS tick re-renders the grid; these
  // walks over tierSales shouldn't re-run for the ~50 cards that didn't change.
  const metrics = React.useMemo(() => calculateMetrics(event), [event]);
  const dateInfo = formatDate(event.date);
  const daysUntil = calculateDaysUntil(event.date);
  const daysDisplay = formatDaysUntil(daysUntil);

  // Detect ticket structure to determine how to display tickets
  const ticketStructure = React.useMemo(() => detectTicketStructure(event.tierSales), [event.tierSales]);
  const isClassicStructure = ticketStructure.type === 'classic' ||
    (ticketStructure.type === 'partial-classic' && ticketStructure.hasGenderSplit);
  const hasStandardCategories = event.tickets.earlyFemale.price > 0 || event.tickets.earlyMale.price > 0 ||
                                 event.tickets.generalFemale.price > 0 || event.tickets.generalMale.price > 0;

  // Show tiered pricing note for non-classic events (events without Early Arrival tiers)
  // Classic events have Early + General tiers, non-classic events only have tiered pricing
  const hasTieredPricing = !isClassicStructure;

  // Get the sections for this specific event
  const eventSections = sectionExpanded?.[event.id] || {};

  // Use dynamic row span hook
  const { rowSpan, cardRef } = useDynamicRowSpan(isExpanded, [eventSections, matchedCampaigns.length]);

  // Drag and drop
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: event.id });

  // iOS-optimized drag style: no opacity, disabled transition during drag
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition, // Disable transition during drag to prevent stuttering
    willChange: 'transform', // Always hint the browser for better performance
    backfaceVisibility: 'hidden', // Prevents flickering on iOS
    WebkitFontSmoothing: 'antialiased', // Smoother text rendering during transform
  };

  return (
    <div
      ref={(node) => {
        cardRef.current = node;
        setNodeRef(node);
      }}
      data-event-id={event.id}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md border transition-all ${event.isHeaderOnly ? 'border-gray-200 dark:border-gray-700' : 'border-gray-300 dark:border-gray-600'} ${isExpanded ? 'expanded-card' : '[content-visibility:auto] [contain-intrinsic-size:auto_200px]'} ${hasRecentUpdate ? 'animate-flash' : ''} ${isPastEvent ? 'opacity-80' : ''}`}
      style={{
        ...(isExpanded ? { gridRow: `span ${rowSpan}` } : {}),
        ...style,
      }}
    >
      {/* Event Header - Always Visible */}
      <div className="p-3">
        <div className="relative">
          {/* Drag Handle - Absolute positioned on left (hidden for past events) */}
          {!isPastEvent && (
          <div
            {...attributes}
            {...listeners}
            className="absolute left-0 top-0 h-full flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors touch-none"
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          </div>
          )}

          {/* Main content with left margin for drag handle */}
          <div className="ml-4 flex flex-col gap-0">
            {/* Row 1: Title + Action Buttons */}
            <div className="grid grid-cols-[1fr_auto] items-start gap-2">
              <div
                className={`flex-1 min-w-0 overflow-hidden ${!event.isHeaderOnly ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors rounded -ml-1 -mt-1 pl-1 pt-1 pb-1' : ''}`}
                onClick={() => {
                  if (!event.isHeaderOnly) {
                    onToggleExpansion();
                  }
                }}
              >
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 w-full truncate">
                  {event.title}
                  {isPastEvent && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded align-middle">
                      Ended
                    </span>
                  )}
                  {event.source === 'universe+tixr' && (
                    <>
                      <span className="ml-1 inline-block px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded align-middle">
                        Universe
                      </span>
                      <span className="ml-1 inline-block px-1.5 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded align-middle">
                        Tixr
                      </span>
                    </>
                  )}
                  {event.source === 'tixr' && (
                    <span className="ml-1 inline-block px-1.5 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded align-middle">
                      Tixr
                    </span>
                  )}
                </h3>
              </div>

              {/* Action Buttons - Row 1 */}
              <div className="flex items-center gap-1 justify-self-end">
                  {/* Live now, widget sessions in-flight (promoted from the
                      date row: the "happening NOW" signal lives next to the
                      title/status dot, and the date row stops overflowing on
                      phones when live + day + %Sold stacked up) */}
                  {widgetStats?.liveNow > 0 && (
                    <div
                      title={`${widgetStats.liveNow} browsing the widget right now`}
                      className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 flex items-center gap-1"
                    >
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {widgetStats.liveNow} live
                    </div>
                  )}
                  {/* Expand/Collapse button */}
                  {!event.isHeaderOnly && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpansion();
                      }}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      )}
                    </button>
                  )}

                  {/* Live indicator and Refresh button (hidden for past events) */}
                  {!isPastEvent && wsConnected && !event.isHeaderOnly && (
                    <div className="relative" title="Live updates active">
                      {/* Steady state: composited pulse only (measured ~free). The old
                          always-on ping ring here cost ~4% main-thread CPU across 40 cards. */}
                      <div className={`w-2 h-2 bg-green-500 rounded-full ${hasRecentUpdate ? 'animate-ping' : 'animate-pulse'}`}></div>
                    </div>
                  )}
                  {!isPastEvent && !isSubscribed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRefresh(true);
                      }}
                      disabled={isLoading || !event.timeslotId}
                      className="bg-blue-600 dark:bg-blue-700 text-white p-1.5 rounded hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 flex items-center transition-colors relative"
                      title={wsConnected ? "Force refresh (live updates active)" : "Refresh event data"}
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  )}
              </div>
            </div>

            {/* Row 2: Date/Link/Campaigns + Status Badges */}
            <div className="grid grid-cols-[1fr_auto] items-start gap-2">
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 flex-shrink-0" />
                  <span className="whitespace-nowrap">
                    {dateInfo.dayName !== 'TBD'
                      ? `${dateInfo.dayName}, ${dateInfo.month} ${dateInfo.day}${isPastEvent ? `, ${dateInfo.year}` : ''}`
                      : 'Date TBD'
                    }
                  </span>
                </div>
                {event.slug && event.slug !== '...' && (
                  <>
                    <span className="text-gray-400 dark:text-gray-600">•</span>
                    <a
                      href={`https://www.universe.com/events/${event.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 whitespace-nowrap transition-colors"
                      title="View event on Universe"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </>
                )}
                {matchedCampaigns.length > 0 && (
                  <>
                    <span className="text-gray-400 dark:text-gray-600">•</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isExpanded) {
                          onToggleExpansion();
                        }
                        // Force open the Meta Campaigns section
                        onToggleSection(event.id, 'metaCampaigns', true);
                        // Scroll to the Meta Campaigns section after a brief delay
                        setTimeout(() => {
                          const metaSection = document.querySelector(`[data-meta-section="${event.id}"]`);
                          if (metaSection) {
                            metaSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                          }
                        }, 100);
                      }}
                      className="flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 whitespace-nowrap transition-colors"
                    >
                      <Target className="w-3 h-3 mr-1" />
                      {matchedCampaigns.length}
                    </button>
                  </>
                )}
                {/* Live door occupancy flip (security check-in), only on the
                    event with an active security link tonight. Placement per
                    user: date row, after the meta indicator, away from the
                    expand chevron. */}
                {occupancyAvailable && (
                  <>
                    <span className="text-gray-400 dark:text-gray-600">•</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleFlip && onToggleFlip(); }}
                      title="Flip to live door occupancy (security check-in)"
                      className="flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 whitespace-nowrap transition-colors"
                    >
                      <Shield className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>

              {/* Status Badges - Row 2 */}
              <div className="flex items-center gap-1 justify-self-end">
                  {daysDisplay && daysUntil !== null && (
                    <div className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                      daysUntil === 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 animate-pulse' :
                      daysUntil === 1 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                      daysUntil > 0 && daysUntil <= 3 ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400' :
                      daysUntil > 3 && daysUntil <= 7 ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400' :
                      daysUntil > 7 && daysUntil <= 30 ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' :
                      daysUntil < 0 ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400' :
                      'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {daysDisplay}
                    </div>
                  )}

                  {/* Sold percentage or Waitlist status */}
                  {!event.isHeaderOnly && (
                    metrics.isWaitlisted ? (
                      <div className="px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 animate-pulse">
                        Waitlisted
                        {event.waitlistCount > 0 && (
                          <span className="ml-1">({event.waitlistCount})</span>
                        )}
                      </div>
                    ) : (
                      <div className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                        metrics.soldPct < 50
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                          : metrics.soldPct < 80
                          ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                      }`} title={`${event.sold} of ${event.capacity} tickets sold`}>
                        {metrics.soldPct}% Sold
                      </div>
                    )
                  )}
              </div>
            </div>
          </div>
        </div>
        {/* End of header */}

        {/* Quick Stats - Always visible for loaded events */}
        {!event.isHeaderOnly && (
          <div className="mt-3">
            {/* Stats Grid - Full Width */}
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded relative">
                <div className="flex items-center justify-center gap-1 mb-1 h-4">
                  <Users className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Sold</span>
                </div>
                <div className="text-lg font-bold text-blue-900 dark:text-blue-100 h-7 flex items-center justify-center">
                  {formatNumber(event.sold)}
                  {hasRecentUpdate && ticketChange > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-full animate-pulse">
                      +{ticketChange}
                    </span>
                  )}
                </div>
              </div>
              
              <div 
                className="bg-green-50 dark:bg-green-900/20 p-2 rounded cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleRevenueDisplay();
                }}
                title={`Click to toggle revenue display`}
              >
                <div className="flex items-center justify-center gap-1 mb-1 h-4">
                  <CircleDollarSign className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-green-600 dark:text-green-400 whitespace-nowrap">
                    {revenueDisplayType === 'base' ? 'Base Rev' : revenueDisplayType === 'allin' ? 'All-in Rev' : 'Revenue'}
                  </span>
                </div>
                <div className="text-lg font-bold text-green-900 dark:text-green-100 h-7 flex items-center justify-center">
                  {formatCurrencyCompact(
                    revenueDisplayType === 'base' ? (event.baseRevenue || 0) : 
                    revenueDisplayType === 'allin' ? (event.allInRevenue || 0) : 
                    event.revenue
                  )}
                </div>
              </div>
              
              <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded">
                <div className="flex items-center justify-center gap-1 mb-1 h-4">
                  <Ticket className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-purple-600 dark:text-purple-400">Avg</span>
                </div>
                <div className="text-lg font-bold text-purple-900 dark:text-purple-100 h-7 flex items-center justify-center">
                  {formatCurrency(event.sold > 0 ? Math.round((
                    revenueDisplayType === 'base' ? (event.baseRevenue || 0) : 
                    revenueDisplayType === 'allin' ? (event.allInRevenue || 0) : 
                    event.revenue
                  ) / event.sold) : 0)}
                </div>
              </div>
              
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded">
                <div className="flex items-center justify-center gap-1 mb-1 h-4">
                  <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">Ratio</span>
                </div>
                {/* ratio is null when no tier carries a gender signal, show a dash, never a fabricated 50:50 */}
                <div className="text-lg font-bold text-indigo-900 dark:text-indigo-100 h-7 flex items-center justify-center">{metrics.ratio || '—'}</div>
              </div>
            </div>
            
            {/* Last refresh time */}
            {event._cachedAt && (
              <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mt-2">
                <Clock className="w-3 h-3" />
                <span className="text-xs">Last updated: {formatTime(new Date(event._cachedAt))}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {!event.isHeaderOnly && isExpanded && (
        <div className="border-t dark:border-gray-700 px-3 pb-3">
          
          {/* Ticket Pricing with toggle */}
          <div className="mt-3 mb-3">
            <div 
              className="flex items-center justify-between mb-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSection(event.id, 'ticketPrices');
              }}
            >
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 flex items-center">
                Ticket Prices {showAllInPrices && <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(All-In with Fees)</span>}
              </h4>
              <div className="flex items-center gap-1">
                {eventSections.ticketPrices !== false && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTogglePriceDisplay();
                    }}
                    className="bg-gray-600 dark:bg-gray-700 text-white px-2 py-0.5 rounded text-xs hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center transition-colors"
                  >
                    <DollarSign className="w-3 h-3 mr-0.5" />
                    {showAllInPrices ? 'All-In' : 'Base'}
                  </button>
                )}
                <div className="p-0.5">
                  {eventSections.ticketPrices === false ? (
                    <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            {eventSections.ticketPrices !== false && (
              <div>
                {/* Show tiered pricing breakdown or classic pricing */}
                {hasTieredPricing ? (
                  <div className="grid grid-cols-2 gap-1.5 text-xs">
                    {/* Display groups in 2-column grid like classic */}
                    {Object.keys(ticketStructure.tieredGroups).length > 0 ? (
                      <>
                        {Object.entries(ticketStructure.tieredGroups)
                          .sort(([keyA, dataA], [keyB, dataB]) => {
                            // Sort by entry type first
                            const entryTypeCompare = dataA.entryType.localeCompare(dataB.entryType);
                            if (entryTypeCompare !== 0) {
                              return entryTypeCompare;
                            }
                            // Within same entry type, Female before Male
                            return dataA.gender === 'Female' ? -1 : 1;
                          })
                          .map(([groupKey, groupData]) => {
                            // Determine background color based on gender
                            const bgColor = groupData.gender === 'Female'
                              ? 'bg-pink-50 dark:bg-pink-900/20'
                              : 'bg-blue-50 dark:bg-blue-900/20';

                            const textColor = groupData.gender === 'Female'
                              ? 'text-pink-700 dark:text-pink-400'
                              : 'text-blue-700 dark:text-blue-400';

                            // Get price based on showAllInPrices toggle
                            let displayPrice = null;
                            if (groupData.currentTierName && event.tierSales[groupData.currentTierName]) {
                              const tierData = event.tierSales[groupData.currentTierName];
                              displayPrice = showAllInPrices ? tierData.displayPrice : tierData.price;
                            } else if (groupData.currentPrice !== null) {
                              displayPrice = groupData.currentPrice;
                            }

                            // Calculate availability for current tier
                            let available = -1;
                            if (groupData.currentTierName && event.tierSales[groupData.currentTierName]) {
                              const tierData = event.tierSales[groupData.currentTierName];
                              available = tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1;
                            }

                            return (
                              <div key={groupKey} className={`${bgColor} p-1.5 rounded`}>
                                <div className="flex items-center justify-between gap-1 flex-nowrap">
                                  <span
                                    className={`${textColor} font-medium`}
                                  >
                                    {groupData.gender} ({groupData.entryType}):
                                  </span>
                                  {groupData.allSoldOut ? (
                                    <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>
                                  ) : displayPrice !== null ? (
                                    <div className="flex items-center gap-0.5">
                                      <span className="dark:text-white whitespace-nowrap">
                                        {formatCurrencyWithDecimals(displayPrice)}
                                      </span>
                                      {available > 0 && (
                                        <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                          {available}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="dark:text-white whitespace-nowrap">
                                      {groupData.minPrice === groupData.maxPrice
                                        ? formatCurrencyWithDecimals(groupData.minPrice)
                                        : `${formatCurrencyWithDecimals(groupData.minPrice)} - ${formatCurrencyWithDecimals(groupData.maxPrice)}`
                                      }
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                        {/* VIP with col-span-2 like classic */}
                        {ticketStructure.hasVIP && event.tickets.vip.price >= 0 && (
                          <div className="col-span-2 flex justify-center">
                            <div className="bg-purple-100 dark:bg-purple-900/30 px-12 py-1.5 rounded">
                              <div className="flex items-center justify-center gap-1 flex-nowrap">
                                <span className="text-purple-700 dark:text-purple-400 font-medium whitespace-nowrap">VIP:</span>
                                {(() => {
                                  // Find matching tier for VIP
                                  const vipTier = event.tierSales && Object.entries(event.tierSales).find(([tierName]) =>
                                    tierName.toLowerCase().includes('vip')
                                  );
                                  if (vipTier) {
                                    const [, tierData] = vipTier;
                                    const available = tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1;
                                    if (available === 0) {
                                      return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                                    }
                                    return (
                                      <div className="flex items-center gap-0.5">
                                        <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.vip.displayPrice : event.tickets.vip.price)}</span>
                                        {available > 0 && (
                                          <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                            {available}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  }
                                  return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.vip.displayPrice : event.tickets.vip.price)}</span>;
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="col-span-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                          <span className="font-semibold">This event uses custom pricing.</span> See <span className="font-semibold">"Sales by Tier"</span> below for complete details.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                <div className="grid grid-cols-2 gap-1.5 text-xs">
              {/* Rows render in the model's category order (entryRowOrder) so a
                  tenant's config can reorder them; default order = the original
                  hardcoded layout (rsvp, early, general, vip). Inner JSX is the
                  untouched original blocks. */}
              {entryRowOrder(getTicketModel()).map((entryType) => { switch (entryType) {
              case 'rsvp': return (<React.Fragment key="rsvp">
              {/* RSVP Early Arrival - Female */}
              {event.tickets.rsvpEarlyFemale && (event.tickets.rsvpEarlyFemale.sold > 0 || event.tickets.rsvpEarlyFemale.available > 0) && (
                <div className={`${cc('rsvpEarlyFemale').bg} p-1.5 rounded`}>
                  <div className="flex items-center justify-between gap-1 flex-nowrap">
                    <span className={`${cc('rsvpEarlyFemale').text} font-medium whitespace-nowrap`}>{cl('rsvpEarlyFemale')}:</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-green-600 dark:text-green-300 whitespace-nowrap">Free</span>
                      {event.tickets.rsvpEarlyFemale.available > 0 && (
                        <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                          {event.tickets.rsvpEarlyFemale.available}
                        </span>
                      )}
                      {event.tickets.rsvpEarlyFemale.available === 0 && event.tickets.rsvpEarlyFemale.sold > 0 && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>
                      )}
                      {hasRecentUpdate && categoryChange.rsvpEarlyFemale > 0 && (
                        <span className="text-xs font-semibold text-green-700 dark:text-green-300 animate-pulse">
                          +{categoryChange.rsvpEarlyFemale}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* RSVP Early Arrival - Male */}
              {event.tickets.rsvpEarlyMale && (event.tickets.rsvpEarlyMale.sold > 0 || event.tickets.rsvpEarlyMale.available > 0) && (
                <div className={`${cc('rsvpEarlyMale').bg} p-1.5 rounded`}>
                  <div className="flex items-center justify-between gap-1 flex-nowrap">
                    <span className={`${cc('rsvpEarlyMale').text} font-medium whitespace-nowrap`}>{cl('rsvpEarlyMale')}:</span>
                    <div className="flex items-center gap-0.5">
                      <span className="text-green-600 dark:text-green-300 whitespace-nowrap">Free</span>
                      {event.tickets.rsvpEarlyMale.available > 0 && (
                        <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                          {event.tickets.rsvpEarlyMale.available}
                        </span>
                      )}
                      {event.tickets.rsvpEarlyMale.available === 0 && event.tickets.rsvpEarlyMale.sold > 0 && (
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>
                      )}
                      {hasRecentUpdate && categoryChange.rsvpEarlyMale > 0 && (
                        <span className="text-xs font-semibold text-green-700 dark:text-green-300 animate-pulse">
                          +{categoryChange.rsvpEarlyMale}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </React.Fragment>);
              case 'early': return (<React.Fragment key="early">
              {(() => {
                // Check if paid early female tier exists in tierSales (exclude RSVP)
                const hasEarlyFemaleTier = event.tierSales && Object.keys(event.tierSales).some(tierName =>
                  (tierName.includes('Early Arrival') || (tierName.includes('Early') && !tierName.includes('General Admission Tier'))) && tierName.includes('Female') && !tierName.toLowerCase().includes('rsvp')
                );
                if (event.tickets.earlyFemale.price > 0 || (hasEarlyFemaleTier && event.tickets.earlyFemale.sold > 0)) {
                  return (
                    <div className={`${cc('earlyFemale').bg} p-1.5 rounded`}>
                      <div className="flex items-center justify-between gap-1 flex-nowrap">
                        <span className={`${cc('earlyFemale').text} font-medium whitespace-nowrap`}>{cl('earlyFemale')}:</span>
                        {(() => {
                          // Find matching tier for Early Female - must have "Early" and NOT be a general tier or RSVP
                          const earlyFemaleTier = event.tierSales && Object.entries(event.tierSales).find(([tierName]) =>
                            (tierName.includes('Early Arrival') || (tierName.includes('Early') && !tierName.includes('General Admission Tier'))) && tierName.includes('Female') && !tierName.toLowerCase().includes('rsvp')
                          );
                          if (earlyFemaleTier) {
                            const [, tierData] = earlyFemaleTier;
                            const available = tierData.available >= 0 ? tierData.available : (tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1);
                            if (available === 0) {
                              return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                            }
                            return (
                              <div className="flex items-center gap-0.5">
                                <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.earlyFemale.displayPrice : event.tickets.earlyFemale.price)}</span>
                                {available > 0 && (
                                  <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                    {available}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.earlyFemale.displayPrice : event.tickets.earlyFemale.price)}</span>;
                        })()}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {(() => {
                // Check if paid early male tier exists in tierSales (exclude RSVP)
                const hasEarlyMaleTier = event.tierSales && Object.keys(event.tierSales).some(tierName =>
                  (tierName.includes('Early Arrival') || (tierName.includes('Early') && !tierName.includes('General Admission Tier'))) &&
                  (tierName.includes('Male') || tierName.includes('Men')) &&
                  !tierName.includes('Female') && !tierName.includes('Women') && !tierName.toLowerCase().includes('rsvp')
                );
                if (event.tickets.earlyMale.price > 0 || (hasEarlyMaleTier && event.tickets.earlyMale.sold > 0)) {
                  return (
                    <div className={`${cc('earlyMale').bg} p-1.5 rounded`}>
                      <div className="flex items-center justify-between gap-1 flex-nowrap">
                        <span className={`${cc('earlyMale').text} font-medium whitespace-nowrap`}>{cl('earlyMale')}:</span>
                        {(() => {
                          // Find matching tier for Early Male - must have "Early" and NOT be a general tier or RSVP
                          const earlyMaleTier = event.tierSales && Object.entries(event.tierSales).find(([tierName]) =>
                            (tierName.includes('Early Arrival') || (tierName.includes('Early') && !tierName.includes('General Admission Tier'))) &&
                            (tierName.includes('Male') || tierName.includes('Men')) &&
                            !tierName.includes('Female') && !tierName.includes('Women') && !tierName.toLowerCase().includes('rsvp')
                          );
                          if (earlyMaleTier) {
                            const [, tierData] = earlyMaleTier;
                            const available = tierData.available >= 0 ? tierData.available : (tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1);
                            if (available === 0) {
                              return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                            }
                            return (
                              <div className="flex items-center gap-0.5">
                                <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.earlyMale.displayPrice : event.tickets.earlyMale.price)}</span>
                                {available > 0 && (
                                  <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                    {available}
                                  </span>
                                )}
                              </div>
                            );
                          }
                          return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.earlyMale.displayPrice : event.tickets.earlyMale.price)}</span>;
                        })()}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              </React.Fragment>);
              case 'general': return (<React.Fragment key="general">
              {event.tickets.generalFemale.price > 0 && (
                <div className={`${cc('generalFemale').bg} p-1.5 rounded`}>
                  <div className="flex items-center justify-between gap-1 flex-nowrap">
                    <span className={`${cc('generalFemale').text} font-medium whitespace-nowrap`}>{cl('generalFemale')}:</span>
                    {(() => {
                      // Find matching tier for General Female - check all general tiers, exclude Early
                      const generalFemaleTiers = event.tierSales && Object.entries(event.tierSales).filter(([tierName]) =>
                        tierName.includes('General Admission Tier') && tierName.includes('Female')
                      );
                      if (generalFemaleTiers && generalFemaleTiers.length > 0) {
                        // Sort by sortIndex to find current active tier
                        const sortedTiers = generalFemaleTiers.sort(([, a], [, b]) => a.sortIndex - b.sortIndex);

                        // Find the first tier with available tickets (available > 0)
                        const activeTier = sortedTiers.find(([, tierData]) => {
                          const available = tierData.maxQuantity - tierData.sold;
                          return available > 0;
                        });

                        if (!activeTier) {
                          // All tiers are sold out
                          return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                        }

                        const [, tierData] = activeTier;
                        const available = tierData.maxQuantity - tierData.sold;
                        return (
                          <div className="flex items-center gap-0.5">
                            <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.generalFemale.displayPrice : event.tickets.generalFemale.price)}</span>
                            {available > 0 && (
                              <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                {available}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.generalFemale.displayPrice : event.tickets.generalFemale.price)}</span>;
                    })()}
                  </div>
                </div>
              )}
              {event.tickets.generalMale.price > 0 && (
                <div className={`${cc('generalMale').bg} p-1.5 rounded`}>
                  <div className="flex items-center justify-between gap-1 flex-nowrap">
                    <span className={`${cc('generalMale').text} font-medium whitespace-nowrap`}>{cl('generalMale')}:</span>
                    {(() => {
                      // Find matching tier for General Male - check all general tiers, exclude Early
                      const generalMaleTiers = event.tierSales && Object.entries(event.tierSales).filter(([tierName]) =>
                        tierName.includes('General Admission Tier') &&
                        (tierName.includes('Male') || tierName.includes('Men')) &&
                        !tierName.includes('Female') && !tierName.includes('Women')
                      );
                      if (generalMaleTiers && generalMaleTiers.length > 0) {
                        // Sort by sortIndex to find current active tier
                        const sortedTiers = generalMaleTiers.sort(([, a], [, b]) => a.sortIndex - b.sortIndex);

                        // Find the first tier with available tickets (available > 0)
                        const activeTier = sortedTiers.find(([, tierData]) => {
                          const available = tierData.maxQuantity - tierData.sold;
                          return available > 0;
                        });

                        if (!activeTier) {
                          // All tiers are sold out
                          return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                        }

                        const [, tierData] = activeTier;
                        const available = tierData.maxQuantity - tierData.sold;
                        return (
                          <div className="flex items-center gap-0.5">
                            <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.generalMale.displayPrice : event.tickets.generalMale.price)}</span>
                            {available > 0 && (
                              <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                {available}
                              </span>
                            )}
                          </div>
                        );
                      }
                      return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.generalMale.displayPrice : event.tickets.generalMale.price)}</span>;
                    })()}
                  </div>
                </div>
              )}
              </React.Fragment>);
              case 'vip': return (<React.Fragment key="vip">
              {/* > 0, not >= 0: price defaults to 0 for events with NO VIP tier, >= rendered a phantom "VIP: $0.00" row on every classic-layout event */}
              {event.tickets.vip.price > 0 && (
                <div className="col-span-2 flex justify-center">
                  <div className={`${cc('vip').bg} px-12 py-1.5 rounded`}>
                    <div className="flex items-center justify-center gap-1 flex-nowrap">
                      <span className={`${cc('vip').text} font-medium whitespace-nowrap`}>{cl('vip')}:</span>
                      {(() => {
                        // Find matching tier for VIP
                        const vipTier = event.tierSales && Object.entries(event.tierSales).find(([tierName]) =>
                          tierName.toLowerCase().includes('vip')
                        );
                        if (vipTier) {
                          const [, tierData] = vipTier;
                          const available = tierData.maxQuantity > 0 ? tierData.maxQuantity - tierData.sold : -1;
                          if (available === 0) {
                            return <span className="text-xs text-red-600 dark:text-red-400 font-medium">(Sold Out)</span>;
                          }
                          return (
                            <div className="flex items-center gap-0.5">
                              <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.vip.displayPrice : event.tickets.vip.price)}</span>
                              {available > 0 && (
                                <span className="px-0.5 py-0 rounded text-[9px] font-medium bg-yellow-200 dark:bg-yellow-700/50 text-yellow-800 dark:text-yellow-300 whitespace-nowrap leading-tight">
                                  {available}
                                </span>
                              )}
                            </div>
                          );
                        }
                        return <span className="dark:text-white whitespace-nowrap">{formatCurrencyWithDecimals(showAllInPrices ? event.tickets.vip.displayPrice : event.tickets.vip.price)}</span>;
                      })()}
                    </div>
                  </div>
                </div>
              )}
              </React.Fragment>);
              default: return null;
              } })}
                </div>
                )}
              </div>
            )}
          </div>

          {/* Sales Breakdown with toggle - Show for all event types */}
          <div className="mb-3">
            <div
              className="flex items-center justify-between mb-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onToggleSection(event.id, 'salesBreakdown');
              }}
            >
              <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">Sales Breakdown</h4>
              <div className="p-0.5">
                {eventSections.salesBreakdown === false ? (
                  <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                ) : (
                  <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                )}
              </div>
            </div>
            {(eventSections.salesBreakdown === undefined || eventSections.salesBreakdown === true) && (
              <div>
                {isClassicStructure ? (
                  <div>
                {(() => {
                  const hasRsvp = event.tickets.rsvpEarlyFemale?.sold > 0 || event.tickets.rsvpEarlyFemale?.available > 0 ||
                                  event.tickets.rsvpEarlyMale?.sold > 0 || event.tickets.rsvpEarlyMale?.available > 0;
                  const gridCols = hasRsvp ? 'grid-cols-8' : 'grid-cols-6';
                  return (
                <div className={`grid ${gridCols} gap-1.5 text-xs mb-2`}>
                  {hasRsvp && (
                    <div className="text-center">
                      <p className="text-green-600 dark:text-green-400 text-xs">RF</p>
                      <div className="flex items-center justify-center gap-0.5">
                        <p className="font-semibold dark:text-gray-100">{event.tickets.rsvpEarlyFemale?.sold || 0}</p>
                        {hasRecentUpdate && categoryChange.rsvpEarlyFemale > 0 && (
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                            +{categoryChange.rsvpEarlyFemale}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {hasRsvp && (
                    <div className="text-center">
                      <p className="text-green-600 dark:text-green-400 text-xs">RM</p>
                      <div className="flex items-center justify-center gap-0.5">
                        <p className="font-semibold dark:text-gray-100">{event.tickets.rsvpEarlyMale?.sold || 0}</p>
                        {hasRecentUpdate && categoryChange.rsvpEarlyMale > 0 && (
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                            +{categoryChange.rsvpEarlyMale}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">EF</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.earlyFemale.sold}</p>
                      {hasRecentUpdate && categoryChange.earlyFemale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.earlyFemale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">EM</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.earlyMale.sold}</p>
                      {hasRecentUpdate && categoryChange.earlyMale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.earlyMale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">GF</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.generalFemale.sold}</p>
                      {hasRecentUpdate && categoryChange.generalFemale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.generalFemale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">GM</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.generalMale.sold}</p>
                      {hasRecentUpdate && categoryChange.generalMale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.generalMale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">VIP</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.vip.sold}</p>
                      {hasRecentUpdate && categoryChange.vip > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.vip}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">COMP</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{event.tickets.comp.sold}</p>
                      {hasRecentUpdate && categoryChange.comp > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.comp}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                  );
                })()}

                {/* Total Female, Male, and Early */}
                <div className="grid grid-cols-3 gap-1.5 text-xs mb-2 border-t dark:border-gray-700 pt-2">
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Total Female</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{metrics.totalFemale}</p>
                      {hasRecentUpdate && categoryChange.totalFemale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.totalFemale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Total Male</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{metrics.totalMale}</p>
                      {hasRecentUpdate && categoryChange.totalMale > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.totalMale}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 text-xs">Total Early</p>
                    <div className="flex items-center justify-center gap-0.5">
                      <p className="font-semibold dark:text-gray-100">{metrics.totalEarly}</p>
                      {hasRecentUpdate && categoryChange.totalEarly > 0 && (
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 animate-pulse">
                          +{categoryChange.totalEarly}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Female to Male Ratio Bar */}
                {(metrics.totalFemale > 0 || metrics.totalMale > 0) && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span>{metrics.totalFemale}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{metrics.ratio}</span>
                      <span>{metrics.totalMale}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full flex">
                        {metrics.totalFemale > 0 && (
                          <div 
                            className="bg-pink-500 dark:bg-pink-400 transition-all duration-300"
                            style={{ 
                              width: `${(metrics.totalFemale / (metrics.totalFemale + metrics.totalMale)) * 100}%` 
                            }}
                          ></div>
                        )}
                        {metrics.totalMale > 0 && (
                          <div 
                            className="bg-blue-500 dark:bg-blue-400 transition-all duration-300"
                            style={{ 
                              width: `${(metrics.totalMale / (metrics.totalFemale + metrics.totalMale)) * 100}%` 
                            }}
                          ></div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-pink-500 dark:bg-pink-400 rounded-full"></div>
                        <span>Female</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
                        <span>Male</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Full Revenue Numbers */}
                <div className="border-t dark:border-gray-700 pt-2 text-xs">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Base Revenue:</span>
                    <span className="font-semibold dark:text-gray-100">{formatCurrency(event.baseRevenue)}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
                    <span className="font-semibold dark:text-gray-100">{formatCurrency(event.revenue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">All-in Revenue <span className="text-xs">(incl. tax)</span>:</span>
                    <span className="font-semibold dark:text-gray-100">{formatCurrency(event.allInRevenue || 0)}</span>
                  </div>
                </div>
              </div>
                ) : (
                  <div>
                    {/* Non-classic breakdown - use same 6-column grid as classic */}
                    {Object.keys(ticketStructure.tieredGroups).length > 0 ? (
                      <>
                        <div className="grid grid-cols-6 gap-1.5 text-xs mb-2">
                          {(() => {
                            // Build array of all items to display in the 6-column grid
                            const gridItems = [];

                            // Add tier groups with abbreviated labels
                            Object.entries(ticketStructure.tieredGroups)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .forEach(([groupKey, groupData]) => {
                                // Calculate total sold for this group
                                const totalSold = groupData.tiers.reduce((sum, tier) => {
                                  const tierData = event.tierSales[tier.name];
                                  return sum + (tierData?.sold || 0);
                                }, 0);

                                // Create abbreviated entry type
                                // Use "GA" for general admission/entry after, otherwise use first letters
                                let entryTypeAbbr;
                                const entryTypeLower = groupData.entryType.toLowerCase();
                                if (entryTypeLower.includes('entry after') || entryTypeLower.includes('general')) {
                                  entryTypeAbbr = 'GA';
                                } else {
                                  entryTypeAbbr = groupData.entryType
                                    .split(' ')
                                    .map(word => word[0])
                                    .join('')
                                    .toUpperCase()
                                    .substring(0, 2);
                                }

                                // Label format: F-OB, M-OB, etc.
                                const label = `${groupData.gender[0]}-${entryTypeAbbr}`;

                                gridItems.push(
                                  <div key={groupKey} className="text-center">
                                    <p className="text-gray-600 dark:text-gray-400 text-xs" title={`${groupData.gender} (${groupData.entryType})`}>{label}</p>
                                    <div className="flex items-center justify-center gap-0.5">
                                      <p className="font-semibold dark:text-gray-100">{totalSold}</p>
                                    </div>
                                  </div>
                                );
                              });

                            // Add VIP if it exists
                            if (ticketStructure.hasVIP) {
                              gridItems.push(
                                <div key="vip" className="text-center">
                                  <p className="text-gray-600 dark:text-gray-400 text-xs">VIP</p>
                                  <div className="flex items-center justify-center gap-0.5">
                                    <p className="font-semibold dark:text-gray-100">{event.tickets.vip.sold}</p>
                                  </div>
                                </div>
                              );
                            }

                            // Add COMP if it exists
                            if (ticketStructure.hasComp) {
                              gridItems.push(
                                <div key="comp" className="text-center">
                                  <p className="text-gray-600 dark:text-gray-400 text-xs">COMP</p>
                                  <div className="flex items-center justify-center gap-0.5">
                                    <p className="font-semibold dark:text-gray-100">{event.tickets.comp.sold}</p>
                                  </div>
                                </div>
                              );
                            }

                            return gridItems;
                          })()}
                        </div>

                        {/* Total Female, Male, and Early */}
                        <div className="grid grid-cols-3 gap-1.5 text-xs mb-2 border-t dark:border-gray-700 pt-2">
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Total Female</p>
                            <div className="flex items-center justify-center gap-0.5">
                              <p className="font-semibold dark:text-gray-100">{metrics.totalFemale}</p>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Total Male</p>
                            <div className="flex items-center justify-center gap-0.5">
                              <p className="font-semibold dark:text-gray-100">{metrics.totalMale}</p>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-400 text-xs">Total Early</p>
                            <div className="flex items-center justify-center gap-0.5">
                              <p className="font-semibold dark:text-gray-100">{metrics.totalEarly}</p>
                            </div>
                          </div>
                        </div>

                        {/* Female to Male Ratio Bar */}
                        {(metrics.totalFemale > 0 || metrics.totalMale > 0) && (
                          <div className="mb-2">
                            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                              <span>{metrics.totalFemale}</span>
                              <span className="font-medium text-gray-900 dark:text-gray-100">{metrics.ratio}</span>
                              <span>{metrics.totalMale}</span>
                            </div>
                            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div className="h-full flex">
                                {metrics.totalFemale > 0 && (
                                  <div
                                    className="bg-pink-500 dark:bg-pink-400 transition-all duration-300"
                                    style={{
                                      width: `${(metrics.totalFemale / (metrics.totalFemale + metrics.totalMale)) * 100}%`
                                    }}
                                  ></div>
                                )}
                                {metrics.totalMale > 0 && (
                                  <div
                                    className="bg-blue-500 dark:bg-blue-400 transition-all duration-300"
                                    style={{
                                      width: `${(metrics.totalMale / (metrics.totalFemale + metrics.totalMale)) * 100}%`
                                    }}
                                  ></div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-pink-500 dark:bg-pink-400 rounded-full"></div>
                                <span>Female</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
                                <span>Male</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Full Revenue Numbers */}
                        <div className="border-t dark:border-gray-700 pt-2 text-xs">
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600 dark:text-gray-400">Base Revenue:</span>
                            <span className="font-semibold dark:text-gray-100">{formatCurrency(event.baseRevenue)}</span>
                          </div>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-600 dark:text-gray-400">Revenue:</span>
                            <span className="font-semibold dark:text-gray-100">{formatCurrency(event.revenue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">All-in Revenue <span className="text-xs">(incl. tax)</span>:</span>
                            <span className="font-semibold dark:text-gray-100">{formatCurrency(event.allInRevenue || 0)}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                          <span className="font-semibold">Custom ticket structure.</span> See <span className="font-semibold">"Sales by Tier"</span> below for complete details.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Engagement, collapsible (like Sales by Tier / Sales by day), below the gender section */}
          {widgetStats && (widgetStats.hasData || widgetStats.liveNow > 0) && (
            <div className="mb-3">
              <div
                className="flex items-center justify-between mb-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
                onClick={(e) => { e.stopPropagation(); onToggleSection(event.id, 'widget'); }}
              >
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">Engagement</h4>
                <div className="flex items-center gap-2">
                  {widgetStats.liveNow > 0 && (
                    <span className="text-[10px] text-green-600 dark:text-green-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>{widgetStats.liveNow} live
                    </span>
                  )}
                  <div className="p-0.5">
                    {eventSections.widget ? (
                      <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
              {eventSections.widget && <WidgetEngagement listingId={event.id} />}
            </div>
          )}

          {/* Sales by Tier */}
          {event.tierSales && Object.keys(event.tierSales).length > 0 && (
            <div className="mb-3">
              <div 
                className="flex items-center justify-between mb-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSection(event.id, 'salesByTier');
                }}
              >
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">Sales by Tier</h4>
                <div className="p-0.5">
                  {eventSections.salesByTier ? (
                    <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  )}
                </div>
              </div>
              {eventSections.salesByTier && (
                <div>
                  {/* Revenue Bar Chart */}
                  {(() => {
                    // Prepare data for the chart - only include tiers with revenue
                    const chartData = Object.entries(event.tierRevenue || {})
                      .filter(([tierName, revenue]) => revenue > 0)
                      .map(([tierName, revenue]) => {
                        // Simplify tier names for the chart
                        let shortName = tierName;
                        
                        // Check for VIP first
                        if (tierName.toLowerCase().includes('vip')) {
                          shortName = 'VIP';
                        } 
                        // Check for Early tickets
                        else if (tierName.toLowerCase().includes('early')) {
                          if (tierName.toLowerCase().includes('female')) {
                            shortName = 'Early F';
                          } else if (tierName.toLowerCase().includes('male')) {
                            shortName = 'Early M';
                          } else {
                            shortName = 'Early';
                          }
                        }
                        // Check for General Admission with tiers
                        else if (tierName.toLowerCase().includes('general admission')) {
                          const tierMatch = tierName.match(/Tier\s+(\d+)/);
                          if (tierMatch) {
                            if (tierName.toLowerCase().includes('female')) {
                              shortName = `T${tierMatch[1]} - F`;
                            } else if (tierName.toLowerCase().includes('male')) {
                              shortName = `T${tierMatch[1]} - M`;
                            } else {
                              shortName = `T${tierMatch[1]}`;
                            }
                          } else {
                            if (tierName.toLowerCase().includes('female')) {
                              shortName = 'Gen F';
                            } else if (tierName.toLowerCase().includes('male')) {
                              shortName = 'Gen M';
                            } else {
                              shortName = 'General';
                            }
                          }
                        }
                        // Check for standalone Tier names
                        else if (tierName.includes('Tier')) {
                          const tierMatch = tierName.match(/Tier\s+(\d+)/);
                          if (tierMatch) {
                            if (tierName.toLowerCase().includes('female')) {
                              shortName = `T${tierMatch[1]} - F`;
                            } else if (tierName.toLowerCase().includes('male')) {
                              shortName = `T${tierMatch[1]} - M`;
                            } else {
                              shortName = `T${tierMatch[1]}`;
                            }
                          }
                        }
                        // Check for other gender-specific tickets
                        else if (tierName.toLowerCase().includes('female')) {
                          shortName = 'F';
                        } else if (tierName.toLowerCase().includes('male')) {
                          shortName = 'M';
                        }
                        // For any other names, truncate if too long
                        else {
                          shortName = tierName.length > 10 ? tierName.substring(0, 8) + '...' : tierName;
                        }
                        
                        return {
                          name: shortName,
                          fullName: tierName,
                          revenue: revenue,
                          baseRevenue: event.tierBaseRevenue?.[tierName] || 0
                        };
                      })
                      .sort((a, b) => b.revenue - a.revenue); // Sort by revenue descending
                    
                    if (chartData.length > 0) {
                      return (
                        <div className="mb-3">
                          <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5 flex items-center gap-1">
                            <BarChart3 className="w-2.5 h-2.5" />
                            Revenue by Tier
                          </h5>
                          <div className="h-[140px] bg-gray-50 dark:bg-gray-700/30 rounded-lg overflow-hidden">
                            <div className="w-full h-full">
                              <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={chartData} margin={{ top: 4, right: 3, left: -2, bottom: 16 }} barCategoryGap="3%">
                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#E5E7EB'} />
                                <XAxis 
                                  dataKey="name"
                                  angle={-45}
                                  textAnchor="end"
                                  height={32}
                                  tick={{ fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                                  stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                                  interval={0}
                                />
                                <YAxis 
                                  tick={{ fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                                  stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                                  tickFormatter={(value) => formatCurrencyCompact(value)}
                                  width={36}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                                    border: `1px solid ${darkMode ? '#374151' : '#E5E7EB'}`,
                                    borderRadius: '6px',
                                    fontSize: '10px'
                                  }}
                                  labelStyle={{ color: darkMode ? '#F3F4F6' : '#111827' }}
                                  formatter={(value) => formatCurrency(value)}
                                  labelFormatter={(label, payload) => {
                                    if (payload && payload[0]) {
                                      return payload[0].payload.fullName;
                                    }
                                    return label;
                                  }}
                                />
                                <Bar 
                                  dataKey={revenueDisplayType === 'base' ? "baseRevenue" : revenueDisplayType === 'allin' ? "allInRevenue" : "revenue"} 
                                  fill={darkMode ? '#06B6D4' : '#0891B2'}
                                  radius={[2, 2, 0, 0]}
                                  maxBarSize={60}
                                />
                              </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Tier Details List */}
                  <div className="space-y-1">
                    {Object.entries(event.tierSales)
                      .sort(([,a], [,b]) => a.sortIndex - b.sortIndex)
                      .map(([tierName, data]) => {
                        const revenue = revenueDisplayType === 'base' 
                          ? (event.tierBaseRevenue?.[tierName] || 0)
                          : revenueDisplayType === 'allin' 
                          ? (event.tierAllInRevenue?.[tierName] || 0)
                          : (event.tierRevenue?.[tierName] || 0);
                        const ticketPrice = showAllInPrices
                          ? data.displayPrice
                          : data.price;
                        const hasSales = data.sold > 0;
                        
                        // Calculate availability
                        const calculatedAvailable = data.maxQuantity > 0 
                          ? data.maxQuantity - data.sold 
                          : -1;
                        
                        return (
                          <div key={tierName} className={`p-1.5 rounded text-xs ${hasSales ? 'bg-gray-50 dark:bg-gray-700/50' : 'bg-gray-50/50 dark:bg-gray-700/30'}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className={`font-medium ${hasSales ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {tierName}
                                </p>
                                <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                                  <span className={hasSales ? '' : 'text-gray-400 dark:text-gray-500'}>
                                    {data.sold} sold
                                    {calculatedAvailable !== -1 && (
                                      <span className={`ml-1 ${
                                        calculatedAvailable === 0 ? 'text-red-600 dark:text-red-400 font-medium' : 
                                        calculatedAvailable < 10 ? 'text-orange-600 dark:text-orange-400' : 
                                        'text-green-600 dark:text-green-400'
                                      }`}>
                                        {calculatedAvailable === 0 ? '(Sold Out)' : `(${calculatedAvailable} left)`}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-gray-400 dark:text-gray-600">•</span>
                                  <span className={`font-medium ${hasSales ? '' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {ticketPrice >= 1000 ? formatCurrencyCompact(ticketPrice) : formatCurrency(ticketPrice || 0)}
                                  </span>
                                </div>
                              </div>
                              {revenue > 0 && (
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatCurrencyCompact(revenue)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Daily Sales Trend with toggle */}
          {event.dailySales && Object.keys(event.dailySales).length > 0 && (
            <div className="mb-3">
              <div 
                className="flex items-center justify-between mb-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-2 px-2 py-1 rounded transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleSection(event.id, 'salesByDay');
                }}
              >
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">Sales by Day</h4>
                <div className="p-0.5">
                  {eventSections.salesByDay ? (
                    <ChevronUp className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  )}
                </div>
              </div>
              {eventSections.salesByDay && (
                <>
                  {/* Sales Chart */}
                  {(() => {
                    // Prepare data for the chart
                    const chartData = Object.entries(event.dailySales)
                      .sort(([a], [b]) => new Date(a + 'T00:00:00') - new Date(b + 'T00:00:00'))
                      .slice(-14) // Last 14 days
                      .map(([date, tickets]) => {
                        const dateObj = new Date(date + 'T00:00:00');
                        // Always use dailyRevenue if dailyBaseRevenue is not available
                        const revenue = revenueDisplayType === 'base' 
                          ? (event.dailyBaseRevenue?.[date] || event.dailyRevenue?.[date] || 0)
                          : revenueDisplayType === 'allin'
                          ? (event.dailyAllInRevenue?.[date] || event.dailyRevenue?.[date] || 0)
                          : (event.dailyRevenue?.[date] || 0);
                        
                        
                        return {
                          date: dateObj.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                          }),
                          fullDate: date,
                          tickets: tickets || 0,
                          revenue: revenue || 0,
                          revenueK: (revenue || 0) / 1000 // Revenue in thousands for better scale
                        };
                      });
                    
                    
                    // Only show chart if we have more than 3 days of data
                    if (chartData.length > 3) {
                      return (
                        <div className="mb-2 bg-gray-50 dark:bg-gray-700/30 rounded-lg overflow-hidden">
                          <div className="flex items-center justify-between mb-1 px-2 pt-2">
                            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center gap-0.5">
                              <BarChart3 className="w-2.5 h-2.5" />
                              14-Day Sales
                            </h5>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {revenueDisplayType === 'base' ? 'Base Revenue' : revenueDisplayType === 'allin' ? 'All-in Revenue' : 'Total Revenue'}
                            </div>
                          </div>
                          <div className="h-[115px] -mx-1">
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={chartData} margin={{ top: 1, right: 1, left: -8, bottom: 5 }}>
                                <defs>
                                  <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={darkMode ? 0.3 : 0.8}/>
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#E5E7EB'} />
                                <XAxis 
                                  dataKey="date" 
                                  tick={{ fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                                  stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                                />
                                <YAxis 
                                  yAxisId="left"
                                  tick={{ fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                                  stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                                  domain={[0, 'dataMax']}
                                  label={{ value: 'Tickets', angle: -90, position: 'insideLeft', style: { fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' } }}
                                />
                                <YAxis 
                                  yAxisId="right"
                                  orientation="right"
                                  tick={{ fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' }}
                                  stroke={darkMode ? '#4B5563' : '#D1D5DB'}
                                  domain={[0, 'dataMax']}
                                  tickFormatter={(value) => formatCurrencyCompact(value)}
                                  label={{ value: 'Revenue', angle: 90, position: 'insideRight', style: { fontSize: 9, fill: darkMode ? '#9CA3AF' : '#6B7280' } }}
                                />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: darkMode ? '#1F2937' : '#FFFFFF',
                                    border: `1px solid ${darkMode ? '#374151' : '#E5E7EB'}`,
                                    borderRadius: '6px',
                                    fontSize: '11px'
                                  }}
                                  labelStyle={{ color: darkMode ? '#F3F4F6' : '#111827' }}
                                  formatter={(value, name) => {
                                    if (name === 'Tickets') return value;
                                    if (name === 'Revenue') return formatCurrencyCompact(value);
                                    return value;
                                  }}
                                />
                                <Area
                                  yAxisId="left"
                                  type="monotone"
                                  dataKey="tickets"
                                  stroke="#3B82F6"
                                  fillOpacity={0.3}
                                  fill="url(#colorTickets)"
                                  strokeWidth={1.5}
                                  name="Tickets"
                                />
                                <Line
                                  yAxisId="right"
                                  type="monotone"
                                  dataKey="revenue"
                                  stroke="#10B981"
                                  strokeWidth={3}
                                  dot={{ fill: '#10B981', r: 3 }}
                                  name="Revenue"
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* Chart Legend */}
                          <div className="flex items-center justify-center gap-3 mt-1 text-xs">
                            <div className="flex items-center gap-1">
                              <div className="w-2.5 h-2.5 bg-blue-500 rounded"></div>
                              <span className="text-gray-600 dark:text-gray-400">Tickets Sold</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-2.5 h-0.5 bg-green-500"></div>
                              <span className="text-gray-600 dark:text-gray-400">Revenue</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Sales List */}
                  <div className="space-y-0.5 text-xs">
                    {(() => {
                      const salesEntries = Object.entries(event.dailySales)
                        .sort(([a], [b]) => new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00'));
                      
                      // Check if today has sales — "today" in the VENUE's
                      // timezone (server day-keys are venue-local; browser
                      // local time flips at the wrong midnight elsewhere).
                      const todayStr = venueTodayStr();

                      // Display up to 14 entries (2 weeks)
                      const displayEntries = [];
                      
                      // First, check if we have today's data
                      const hasTodayData = salesEntries.some(([date]) => date === todayStr);
                      
                      // If we have today's data, show it; otherwise show "No sales yet"
                      if (hasTodayData) {
                        // Today has data, so process normally
                        salesEntries.slice(0, 14).forEach(([date, count]) => {
                          const dateObj = new Date(date + 'T00:00:00');
                          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                          const formattedDate = dateObj.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                          const revenue = revenueDisplayType === 'base' 
                            ? (event.dailyBaseRevenue?.[date] || 0)
                            : revenueDisplayType === 'allin'
                            ? (event.dailyAllInRevenue?.[date] || 0)
                            : (event.dailyRevenue?.[date] || 0);
                          
                          const isToday = date === todayStr;
                          
                          displayEntries.push(
                            <div key={date} className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">
                                {isToday ? 'Today' : `${dayName}, ${formattedDate}`}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium dark:text-gray-100">{count} tickets</span>
                                {revenue > 0 && (
                                  <span className="font-medium text-green-700 dark:text-green-400">{formatCurrencyCompact(revenue)}</span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      } else {
                        // No data for today - check if we should show "No sales yet" based on
                        // event date (venue-local YYYY-MM-DD string compare, same as todayStr)
                        const eventIsToday = event.date === todayStr;
                        const eventIsFuture = event.date > todayStr;
                        
                        // Only show "No sales yet" for today if the event hasn't happened yet or is today
                        if (!eventIsFuture && !eventIsToday) {
                          // Event is in the past, don't add a "today" entry
                        } else {
                          // Event is today or in the future, show "No sales yet" for today
                          displayEntries.push(
                            <div key="today" className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Today</span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-400 dark:text-gray-500 italic">No sales yet</span>
                              </div>
                            </div>
                          );
                        }
                        
                        // Then show the recent sales (up to 13 more to make 14 total)
                        salesEntries.slice(0, 13).forEach(([date, count]) => {
                          const dateObj = new Date(date + 'T00:00:00');
                          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                          const formattedDate = dateObj.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                          const revenue = revenueDisplayType === 'base' 
                            ? (event.dailyBaseRevenue?.[date] || 0)
                            : revenueDisplayType === 'allin'
                            ? (event.dailyAllInRevenue?.[date] || 0)
                            : (event.dailyRevenue?.[date] || 0);
                          
                          displayEntries.push(
                            <div key={date} className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">
                                {`${dayName}, ${formattedDate}`}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium dark:text-gray-100">{count} tickets</span>
                                {revenue > 0 && (
                                  <span className="font-medium text-green-700 dark:text-green-400">{formatCurrencyCompact(revenue)}</span>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }
                      
                      // Add a summary if there are more entries
                      if (salesEntries.length > 14) {
                        const remainingDays = salesEntries.length - 14;
                        const remainingTickets = salesEntries.slice(14).reduce((sum, [, count]) => sum + count, 0);
                        displayEntries.push(
                          <div key="summary" className="flex justify-between border-t dark:border-gray-700 pt-1 mt-1">
                            <span className="text-gray-500 dark:text-gray-400 text-xs italic">
                              {remainingDays} more days
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 text-xs italic">
                              {remainingTickets} tickets
                            </span>
                          </div>
                        );
                      }
                      
                      return displayEntries;
                    })()}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Meta Campaigns Section - MOVED TO BOTTOM */}
          {matchedCampaigns.length > 0 && (
            <MetaCampaigns
              event={event}
              matchedCampaigns={matchedCampaigns}
              expandedSections={sectionExpanded}
              expandedAdSets={expandedAdSets}
              toggleSection={onToggleSection}
              toggleAdSetExpansion={onToggleAdSetExpansion}
            />
          )}
        </div>
      )}

      {/* Load failed state, REST fetch errored after retries */}
      {event.isHeaderOnly && !isLoading && loadError && (
        <div className="text-center py-4 border-t border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-900/10">
          <p className="text-xs text-red-700 dark:text-red-300 mb-2">Load failed: {loadError}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Retry
            </button>
          )}
        </div>
      )}

      {/* Loading State for Header-Only Events */}
      {event.isHeaderOnly && !isLoading && !loadError && (
        <div className="text-center py-4 border-t dark:border-gray-700 text-gray-500 dark:text-gray-400">
          <Ticket className="w-6 h-6 mx-auto mb-1.5 opacity-50" />
          <p className="text-xs">Event details will load automatically</p>
        </div>
      )}

      {/* Loading State when auto-loading */}
      {event.isHeaderOnly && isLoading && (
        <div className="text-center py-4 border-t dark:border-gray-700">
          <RefreshCw className="w-6 h-6 mx-auto mb-1.5 animate-spin text-blue-600 dark:text-blue-400" />
          <p className="text-xs text-gray-600 dark:text-gray-400">Loading event details...</p>
        </div>
      )}
    </div>
  );
};

// Per-event Universe-widget engagement (expanded card, below the gender section).
// Everything (funnel totals, page views, conversion, trend) is derived from ONE
// per-event funnel-daily call at the selected range — sum the series for totals,
// plot it for the trend. Range toggle just changes `days`. Reuses recharts.
const WIDGET_STAGES = [
  { key: 'page_views', label: 'Page views' },
  { key: 'opens', label: 'Opens' },
  { key: 'cart', label: 'Cart' },
  { key: 'info', label: 'Info' },
  { key: 'payment', label: 'Payment' },
  { key: 'purchased', label: 'Purchased' },
];
// ROLLING windows (last N×24h) — same semantics as the ticketing-widget
// analytics page, so "24h" shows the same numbers on both. Widget caps the
// range at 90 days, so "All" = 90 (effectively all-time for a single event's
// sales window).
const WIDGET_RANGES = [
  { key: '1', label: '24h', title: 'Last 24 hours', days: 1, hourly: true },
  { key: '7', label: '7d', title: 'Last 7 days', days: 7 },
  { key: '14', label: '14d', title: 'Last 14 days', days: 14 },
  { key: 'all', label: 'All', title: 'Last 90 days', days: 90 },
];

// Series labels are 'YYYY-MM-DD' (daily) or 'YYYY-MM-DDTHH:00' (hourly).
const isHourLabel = (d) => String(d).includes('T');
const widgetTick = (d) => (isHourLabel(d) ? String(d).slice(11) : String(d).slice(5));
const widgetTooltipLabel = (d) => (isHourLabel(d) ? `${String(d).slice(5, 10)} ${String(d).slice(11)}` : d);

function WidgetEngagement({ listingId }) {
  const [rangeKey, setRangeKey] = useState('7');
  const [series, setSeries] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const range = WIDGET_RANGES.find((r) => r.key === rangeKey);
  const days = range?.days || 7;
  const hourly = !!range?.hourly;

  useEffect(() => {
    let on = true;
    setLoading(true);
    // initial=true shows the spinner + clears it; poll refreshes silently (no
    // flicker, range preserved) so engagement updates live without a page reload.
    // Totals come from the rolling-window funnel; the daily series only feeds
    // the trend chart (day buckets can't express a rolling 24h total).
    const pull = (initial) => Promise.all([
      fetchWidgetFunnel(listingId, days),
      fetchWidgetDaily(listingId, days, hourly),
    ])
      .then(([f, s]) => { if (on) { setTotals(f); setSeries(s); if (initial) setLoading(false); } })
      .catch(() => { if (on && initial) setLoading(false); });
    pull(true);
    const t = setInterval(() => pull(false), 30000); // live-refresh every 30s while expanded
    return () => { on = false; clearInterval(t); };
  }, [listingId, days, hourly]);

  const t = totals || sumWidgetSeries(series);
  const opens = t.opens || 0;
  const convPct = opens > 0 ? (t.purchased / opens) * 100 : 0;
  const max = Math.max(t.page_views, t.opens, 1);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="inline-flex rounded border border-gray-200 dark:border-gray-700 overflow-hidden text-[11px]">
          {WIDGET_RANGES.map((r) => (
            <button
              key={r.key}
              title={r.title}
              onClick={(e) => { e.stopPropagation(); setRangeKey(r.key); }}
              className={`px-2 py-0.5 ${rangeKey === r.key ? 'bg-indigo-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Views <span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(t.page_views)}</span></span>
          <span className="text-gray-600 dark:text-gray-400">Opens <span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(opens)}</span></span>
          <span className="text-gray-600 dark:text-gray-400">Conv <span className="font-semibold text-emerald-600 dark:text-emerald-400">{convPct.toFixed(1)}%</span></span>
        </div>
      </div>

      {loading && !series ? (
        <div className="text-[11px] text-gray-400 py-2">Loading…</div>
      ) : (
        <>
          <div className="space-y-1 mb-2">
            {WIDGET_STAGES.map((st) => {
              const v = t[st.key] || 0;
              const pct = (v / max) * 100;
              return (
                <div key={st.key} className="flex items-center gap-2 text-[11px]">
                  <span className="w-20 text-gray-500 dark:text-gray-400">{st.label}</span>
                  <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                    <div className="h-full bg-indigo-400 dark:bg-indigo-500" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="w-10 text-right font-medium text-gray-700 dark:text-gray-300">{formatNumber(v)}</span>
                </div>
              );
            })}
          </div>

          {series && series.length > 0 && (
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={series} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" className="text-gray-200 dark:text-gray-700" stroke="currentColor" />
                  <XAxis dataKey="day" tick={{ fontSize: 9 }} tickFormatter={widgetTick} />
                  <YAxis tick={{ fontSize: 9 }} width={28} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 11 }} labelFormatter={widgetTooltipLabel} />
                  <Line type="monotone" dataKey="page_views" stroke="#94a3b8" strokeWidth={1.5} dot={false} name="Views" />
                  <Line type="monotone" dataKey="opens" stroke="#6366f1" strokeWidth={1.5} dot={false} name="Opens" />
                  <Line type="monotone" dataKey="purchased" stroke="#10b981" strokeWidth={1.5} dot={false} name="Purchased" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// React.memo: a WS tick updates ONE event object; unchanged cards keep their
// previous event reference and skip re-render entirely.
export default React.memo(EventCard);
